import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import {
  ACQUISITION_IMPORT_STAGES,
  AcquisitionFullnameOption,
  AcquisitionRun,
  displayAcquisitionRequestId,
  getAcquisitionStageSpreadsheetFields,
} from "models/acquisition-import";
import { useAcquisition } from "src/hooks/acquisition-import/useAcquisition";
import { useCreateAcquisitionRun } from "src/hooks/acquisition-import/useCreateAcquisitionRun";
import { useAcquisitionFullnameOptions } from "src/hooks/acquisition-import/useAcquisitionFullnameOptions";
import StatusChip from "../general/StatusChip";
import {
  AcquisitionRecordRow,
  formatAcquisitionListDate,
} from "./AcquisitionRecordRow";

const fullnameFilter = createFilterOptions<AcquisitionFullnameOption>({
  stringify: (option) =>
    [option.label, option.fullname, option.id].filter(Boolean).join(" "),
});

function groupRunsByStage(runs: AcquisitionRun[]): {
  code: string;
  label: string;
  runs: AcquisitionRun[];
}[] {
  const byCode = new Map<string, AcquisitionRun[]>();
  for (const run of runs) {
    const code = run.input.stageCode || "unknown";
    const list = byCode.get(code) || [];
    list.push(run);
    byCode.set(code, list);
  }
  const groups: { code: string; label: string; runs: AcquisitionRun[] }[] = [];
  for (const stage of ACQUISITION_IMPORT_STAGES) {
    const stageRuns = byCode.get(stage.code);
    if (stageRuns && stageRuns.length > 0) {
      groups.push({
        code: stage.code,
        label: stage.label,
        runs: stageRuns,
      });
      byCode.delete(stage.code);
    }
  }
  byCode.forEach((stageRuns, code) => {
    groups.push({
      code,
      label: stageRuns[0]?.input.stageLabel || code,
      runs: stageRuns,
    });
  });
  return groups;
}

const AcquisitionImport: React.FC = () => {
  const { acquisitionId = "" } = useParams<{ acquisitionId: string }>();
  const navigate = useNavigate();
  const { acquisition, runs, loading, refetch } = useAcquisition(acquisitionId);
  const {
    createRun,
    loading: creating,
    error,
    setError,
  } = useCreateAcquisitionRun();
  const classifications = useAcquisitionFullnameOptions("classifications");
  const departments = useAcquisitionFullnameOptions("departments");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stageCode, setStageCode] = useState(
    ACQUISITION_IMPORT_STAGES[0]?.code || ""
  );
  const [urlByKey, setUrlByKey] = useState<Record<string, string>>({});
  const [classOption, setClassOption] =
    useState<AcquisitionFullnameOption | null>(null);
  const [departmentOption, setDepartmentOption] =
    useState<AcquisitionFullnameOption | null>(null);
  const [invoiceForm, setInvoiceForm] = useState("");
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("");
  const [productName, setProductName] = useState("");
  const [jiraTicket, setJiraTicket] = useState("");
  const [comment, setComment] = useState("");
  const spreadsheetFields = getAcquisitionStageSpreadsheetFields(stageCode);
  const isBilling = stageCode === "billing_accounts";
  const isPricePlan = stageCode === "price_plans";
  const isPriceBook = stageCode === "price_books";
  const isSubscriptions = stageCode === "subscriptions";
  const isContractualDocs = stageCode === "contractual_docs";
  const isLinkContractualDocs = stageCode === "link_contractual_docs";
  const isPendingActivation = stageCode === "pending_activation_lines";
  const isPriceCatalog = isPricePlan || isPriceBook;
  const needsSubscriptionPlanId = isPriceCatalog || isSubscriptions;
  const needsClassDepartment = isBilling || isSubscriptions;
  const needsProductName =
    isSubscriptions || isContractualDocs || isLinkContractualDocs;
  const needsFinSheets =
    isBilling ||
    isSubscriptions ||
    isContractualDocs ||
    isLinkContractualDocs ||
    isPendingActivation;

  useEffect(() => {
    const hasActive = runs.some(
      (run) => run.status === "QUEUED" || run.status === "IN_PROGRESS"
    );
    if (!hasActive) return;
    const intervalId = setInterval(() => refetch(true), 5000);
    return () => clearInterval(intervalId);
  }, [runs, refetch]);

  const loadBillingDropdowns = () => {
    classifications.load();
    departments.load();
  };

  const resetDialog = () => {
    setUrlByKey({});
    setClassOption(null);
    setDepartmentOption(null);
    setInvoiceForm("");
    setSubscriptionPlanId("");
    setProductName("");
    setJiraTicket("");
    setComment("");
  };

  const urlsReady = spreadsheetFields.every(
    (field) => String(urlByKey[field.key] || "").trim() !== ""
  );
  const billingReady =
    !isBilling || invoiceForm.trim() !== "";
  const classDepartmentReady =
    !needsClassDepartment ||
    (Boolean(classOption) && Boolean(departmentOption));
  const pricePlanReady =
    !needsSubscriptionPlanId || /^\d+$/.test(subscriptionPlanId.trim());
  const subscriptionsReady =
    !needsProductName || productName.trim() !== "";
  const dropdownsLoading =
    needsClassDepartment && (classifications.loading || departments.loading);

  const handleCreate = async () => {
    const firstUrl = String(
      urlByKey[spreadsheetFields[0]?.key || "spreadsheetUrl"] || ""
    ).trim();
    const result = await createRun(acquisitionId, {
      stageCode,
      spreadsheetUrl: needsFinSheets
        ? String(urlByKey.fin8SpreadsheetUrl || "").trim()
        : firstUrl,
      ...(isBilling
        ? {
            fin8SpreadsheetUrl: String(urlByKey.fin8SpreadsheetUrl || "").trim(),
            fin28SpreadsheetUrl: String(
              urlByKey.fin28SpreadsheetUrl || ""
            ).trim(),
            classFullname: classOption?.fullname || "",
            departmentFullname: departmentOption?.fullname || "",
            invoiceForm: invoiceForm.trim(),
          }
        : {}),
      ...(isSubscriptions
        ? {
            fin8SpreadsheetUrl: String(urlByKey.fin8SpreadsheetUrl || "").trim(),
            fin27SpreadsheetUrl: String(
              urlByKey.fin27SpreadsheetUrl || ""
            ).trim(),
            subscriptionPlanId: subscriptionPlanId.trim(),
            productName: productName.trim(),
            classFullname: classOption?.fullname || "",
            departmentFullname: departmentOption?.fullname || "",
          }
        : {}),
      ...(isContractualDocs || isLinkContractualDocs
        ? {
            fin8SpreadsheetUrl: String(urlByKey.fin8SpreadsheetUrl || "").trim(),
            fin27SpreadsheetUrl: String(
              urlByKey.fin27SpreadsheetUrl || ""
            ).trim(),
            productName: productName.trim(),
          }
        : {}),
      ...(isPendingActivation
        ? {
            fin8SpreadsheetUrl: String(urlByKey.fin8SpreadsheetUrl || "").trim(),
            fin28SpreadsheetUrl: String(
              urlByKey.fin28SpreadsheetUrl || ""
            ).trim(),
          }
        : {}),
      ...(isPriceCatalog
        ? { subscriptionPlanId: subscriptionPlanId.trim() }
        : {}),
      ...(jiraTicket.trim() ? { jiraTicket: jiraTicket.trim() } : {}),
      ...(comment.trim() ? { comment: comment.trim() } : {}),
    });
    if (result) {
      setDialogOpen(false);
      resetDialog();
      refetch();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!acquisition) {
    return (
      <Container sx={{ pt: 4 }}>
        <Typography>Acquisition not found.</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ pt: 4, pb: 10 }}>
      <Box display="flex" alignItems="center" mb={2} gap={1}>
        <IconButton onClick={() => navigate("/acquisition-imports")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {acquisition.name}
        </Typography>
        {acquisition.lastRunStatus && (
          <StatusChip status={acquisition.lastRunStatus} />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {displayAcquisitionRequestId(acquisition.acquisitionId)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Added {formatAcquisitionListDate(acquisition.createdAt)}
        {acquisition.createdByName || acquisition.createdByEmail
          ? ` - ${acquisition.createdByName || acquisition.createdByEmail}`
          : ""}
      </Typography>
      {acquisition.finSpreadsheetUrl && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          FIN file:{" "}
          <a href={acquisition.finSpreadsheetUrl} target="_blank" rel="noreferrer">
            {acquisition.finSpreadsheetUrl}
          </a>
        </Typography>
      )}
      {acquisition.workingSpreadsheetUrl && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Working spreadsheet:{" "}
          <a
            href={acquisition.workingSpreadsheetUrl}
            target="_blank"
            rel="noreferrer"
          >
            {acquisition.workingSpreadsheetUrl}
          </a>
        </Typography>
      )}
      {acquisition.subsidiary && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Subsidiary: {acquisition.subsidiary}
        </Typography>
      )}
      {acquisition.jiraTicket && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Jira Ticket: {acquisition.jiraTicket}
        </Typography>
      )}
      {acquisition.comment && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Comment: {acquisition.comment}
        </Typography>
      )}
      <Box sx={{ mb: 2 }} />

      <Divider sx={{ mb: 3 }} />
      <Typography variant="h5" gutterBottom>
        Stage runs
      </Typography>
      {runs.length === 0 ? (
        <Typography>No stage runs yet. Queue a stage to start.</Typography>
      ) : (
        groupRunsByStage(runs).map((group, groupIndex) => (
          <Box key={group.code} sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                mt: groupIndex === 0 ? 0 : 1,
                mb: 0.5,
                fontWeight: 700,
              }}
            >
              {group.label}
            </Typography>
            {group.runs.map((run) => {
              const summary = [
                run.input.requesterName || run.input.requesterEmail
                  ? `Requestor: ${run.input.requesterName || run.input.requesterEmail}`
                  : "",
                run.output.createdCount != null
                  ? `Rows: ${run.output.createdCount}`
                  : "",
                run.output.uploadResult === "SUCCESS"
                  ? run.output.alreadyExists &&
                    run.output.alreadyExists.length > 0
                    ? `Upload successful, ${run.output.alreadyExists.length} already exist`
                    : "Upload successful"
                  : run.output.notUploaded && run.output.notUploaded.length > 0
                    ? `${run.output.notUploaded.length} not uploaded`
                    : "",
              ]
                .filter(Boolean)
                .join(" - ");
              return (
                <AcquisitionRecordRow
                  key={run.runId}
                  requestId={displayAcquisitionRequestId(run.runId)}
                  summary={summary}
                  status={run.status}
                  decision={run.output.decision}
                  createdAt={run.createdAt}
                  onClick={() =>
                    navigate(
                      `/acquisition-imports/${acquisitionId}/runs/${run.runId}`
                    )
                  }
                />
              );
            })}
          </Box>
        ))
      )}

      <Fab
        variant="extended"
        color="primary"
        onClick={() => {
          setError(null);
          setDialogOpen(true);
          if (stageCode === "billing_accounts" || stageCode === "subscriptions") {
            loadBillingDropdowns();
          }
        }}
        sx={{ position: "fixed", bottom: 24, right: 24 }}
      >
        <AddIcon sx={{ mr: 1 }} />
        Run Stage
      </Fab>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Queue a stage run</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel id="stage-select-label">Stage</InputLabel>
            <Select
              labelId="stage-select-label"
              label="Stage"
              value={stageCode}
              onChange={(e) => {
                const next = String(e.target.value);
                setStageCode(next);
                if (next === "billing_accounts" || next === "subscriptions") {
                  loadBillingDropdowns();
                }
              }}
            >
              {ACQUISITION_IMPORT_STAGES.map((stage) => (
                <MenuItem key={stage.code} value={stage.code}>
                  {stage.order / 10}. {stage.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {spreadsheetFields.map((field) => (
            <TextField
              key={field.key}
              margin="dense"
              label={field.label}
              helperText={field.helperText || undefined}
              FormHelperTextProps={{ sx: { whiteSpace: "normal" } }}
              fullWidth
              required
              value={urlByKey[field.key] || ""}
              onChange={(e) =>
                setUrlByKey((current) => ({
                  ...current,
                  [field.key]: e.target.value,
                }))
              }
            />
          ))}
          {needsClassDepartment && (
            <>
              <Autocomplete
                options={classifications.items}
                value={classOption}
                onChange={(_event, value) => setClassOption(value)}
                getOptionLabel={(option) => option.label || option.fullname}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={fullnameFilter}
                loading={classifications.loading}
                autoHighlight
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Class"
                    required
                    helperText={
                      classifications.loading
                        ? "Loading classifications..."
                        : "Type to filter, then pick a class"
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {classifications.loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Autocomplete
                options={departments.items}
                value={departmentOption}
                onChange={(_event, value) => setDepartmentOption(value)}
                getOptionLabel={(option) => option.label || option.fullname}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={fullnameFilter}
                loading={departments.loading}
                autoHighlight
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Department"
                    required
                    helperText={
                      departments.loading
                        ? "Loading departments..."
                        : "Type to filter, then pick a department"
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {departments.loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </>
          )}
          {isBilling && (
            <TextField
              margin="dense"
              label="Invoice Form"
              fullWidth
              required
              value={invoiceForm}
              onChange={(e) => setInvoiceForm(e.target.value)}
            />
          )}
          {needsSubscriptionPlanId && (
            <TextField
              margin="dense"
              label="Subscription Plan ID"
              helperText={
                isSubscriptions
                  ? "NetSuite internal id of the subscription plan (digits only). Written to every Subscription CSV row as Subscription Plan Internal ID."
                  : isPriceBook
                  ? "NetSuite internal id of the subscription plan (digits only). One price book per distinct FIN_8 currency. Book lines = plan members. External ID is {Currency} Pricing_{planId}_Pricebook."
                  : "NetSuite internal id of the subscription plan (digits only). One price plan per distinct FIN_8 currency per plan member line. External ID is SUBS_PLAN_{planId}_{currency}_{lineNumber}."
              }
              FormHelperTextProps={{ sx: { whiteSpace: "normal" } }}
              fullWidth
              required
              value={subscriptionPlanId}
              onChange={(e) => setSubscriptionPlanId(e.target.value)}
            />
          )}
          {needsProductName && (
            <TextField
              margin="dense"
              label="Product Name"
              helperText={
                isLinkContractualDocs
                  ? "Used to rebuild Contractual External ID as Contract - {billing customer altname} {product name} {MM-YYYY}."
                  : isContractualDocs
                  ? "Used in Contractual Docs Name as {billing customer altname} {product name} {MM-YYYY}. External ID is Contract - {that name}."
                  : "Used in Sub Name as {billing customer altname} {product name} {MM-YYYY}."
              }
              FormHelperTextProps={{ sx: { whiteSpace: "normal" } }}
              fullWidth
              required
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
          )}
          <TextField
            margin="dense"
            label="Jira Ticket"
            fullWidth
            value={jiraTicket}
            onChange={(e) => setJiraTicket(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Comment"
            fullWidth
            multiline
            minRows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {(error ||
            (needsClassDepartment &&
              (classifications.error || departments.error))) && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error || classifications.error || departments.error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              !urlsReady ||
              !billingReady ||
              !classDepartmentReady ||
              !pricePlanReady ||
              !subscriptionsReady ||
              creating ||
              dropdownsLoading
            }
          >
            {creating ? <CircularProgress size={20} /> : "Queue"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AcquisitionImport;
